import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import clsx from 'clsx'
import Loading from '../../../../molecules/Loading'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import ProgressBar from '../../../../atoms/ProgressBar'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import useReportComponentAccessors from '../../inspector/useReportComponentAccessors'
import { buildPropAccessors } from '../../inspector/buildPropAccessors'

function PipelineAllocationList ({
  pipeline,
  variables,
  seriesAccessor,
  nameAccessor,
  valueAccessor,
  valueFormat,
  allocationFormat,
  defaultColor,
  className,
  style
}) {
  const { formatter } = useFormattingContext()
  const { data, isLoading, inspectorId } = usePipelineDataContextWithVariables(pipeline, variables)

  const reportedAccessors = useMemo(
    () => buildPropAccessors({ nameAccessor, valueAccessor }),
    [nameAccessor, valueAccessor]
  )
  useReportComponentAccessors({
    inspectorId,
    component: 'DDPAllocationList',
    label: typeof pipeline === 'string' ? pipeline : null,
    accessors: reportedAccessors,
    dataAccessor: seriesAccessor
  })

  const seriesData = useMemo(() => {
    const items = get(data, seriesAccessor)
    if (!items?.length) return []

    const series = items.map(item => ({
      name: get(item, nameAccessor),
      value: formatter(get(item, valueAccessor), valueFormat),
      rawValue: get(item, valueAccessor) || 0,
      allocation: null
    }))
    const total = series.reduce((total, item) => item.rawValue + total, 0)
    return series.map(item => {
      const allocationValue = total !== 0 ? item.rawValue / total : 0
      return ({
        ...item,
        allocationValue,
        allocation: formatter(allocationValue, allocationFormat)
      })
    })
  }, [data, seriesAccessor, nameAccessor, valueAccessor, formatter, valueFormat, allocationFormat])

  if (isLoading) {
    return (
      <div className={className} style={style}>
        <Loading />
      </div>
    )
  }

  return (
    <div className={clsx(className, 'flex flex-col gap-md')} style={style}>
      {(seriesData || []).map((d, i) => (
        <div key={`${d.name}-${i}`}>
          <div>
            <ProgressBar
              squared
              color={defaultColor}
              backgroundColor='var(--color-neutral-200)'
              percentage={d.allocationValue * 100}
            />
          </div>
          <div className='flex flex-row justify-between gap-sm'>
            <div className='flex flex-row gap-sm'>
              <span>{d.name}</span>
              <span>{d.value}</span>
            </div>
            <div>{d.allocation}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

PipelineAllocationList.propTypes = {
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,
  seriesAccessor: PropTypes.string,
  nameAccessor: PropTypes.string,
  valueAccessor: PropTypes.string,
  valueFormat: PropTypes.string,
  allocationFormat: PropTypes.string,
  defaultColor: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object
}

PipelineAllocationList.defaultProps = {
  seriesAccessor: 'data',
  valueFormat: 'marketValue',
  allocationFormat: 'allocation',
  defaultColor: 'var(--color-primary-500)'
}

export default PipelineAllocationList
